import { makeStyles } from '@material-ui/styles'
import { ephemeralSharedStorageDoc, imageLibraryStorageTypeDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import LearnMoreLink from 'core/components/LearnMoreLink'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Divider from 'core/elements/Divider'
import Dropdown from 'core/elements/dropdown'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { customValidator, requiredValidatorWithMessage } from 'core/utils/fieldValidators'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { listVolumeTypes } from '../../storage/volume-types/actions'
import { volumeTypesSelectorFiltered } from '../../storage/volume-types/selectors'
import { VolumeType } from './ClusterBlueprintPage'
import {
  defaultImageLibraryLocation,
  defaultVmStoragePath,
  ImageLibraryStorageType,
  imageLibraryStorageTypeOptions,
} from './constants'

const useStyles = makeStyles((theme: Theme) => ({
  cardBody: {
    display: 'grid',
    gap: theme.spacing(3),
  },
  withSetToDefault: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
  },
  virtualMachineStorageSection: {
    display: 'grid',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  setToDefault: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),

    '& span': {
      cursor: 'pointer',
    },

    '&:hover': {
      textDecoration: 'underline',
    },
  },
  imageLibraryStorageOptions: {
    display: 'grid',
    gridTemplateColumns: '346px 346px 150px',
    gap: theme.spacing(2),
    alignItems: 'center',

    '& .card-body': {
      padding: `${theme.spacing(2)}px !important`,
    },
  },
  storageCardContent: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
  infoText: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  divider: {
    margin: theme.spacing(1, 0, 1),
  },
  tooltipLink: {
    color: theme.palette.grey['000'],
    '& span': {
      color: theme.palette.grey['000'],
    },
  },
}))

const imageLibraryLocationValidation = customValidator((str, props) => {
  if (!str) {
    return true
  }
  if (str[0] === '/') {
    return true
  }
  if (props?.volumeTypes?.map((type) => type.type)?.includes(str)) {
    return true
  }
  return false
}, 'Must be a filesystem path. Click on Set to default to reset to default path')

interface ClusterDefaultsProps {
  params: any
  getParamsUpdater: (field: string) => (value: any) => void
  imageLibraryAuthorized: boolean
  hypervisorAuthorized: boolean
  imageLibraryStorageType: ImageLibraryStorageType
  handleImageLibraryStorageTypeChange: (value: ImageLibraryStorageType) => void
}

export default function ClusterDefaults({
  params,
  getParamsUpdater,
  imageLibraryAuthorized,
  hypervisorAuthorized,
  imageLibraryStorageType,
  handleImageLibraryStorageTypeChange,
}: ClusterDefaultsProps) {
  const classes = useStyles()

  const { loading: loadingVolumeTypes } = useListAction(listVolumeTypes)
  const existingVolumeTypes = useSelector(volumeTypesSelectorFiltered) as VolumeType[]

  // Create a dropdown list for exiting Volume types and new volume types
  const volumeTypeOptions = useMemo(() => {
    return (
      [...existingVolumeTypes, ...params?.volumeTypes]?.map((vt) => ({
        item: vt?.name,
        value: vt.name,
      })) || []
    )
  }, [existingVolumeTypes, params?.volumeTypes])

  return (
    <FormFieldCard title="Cluster Defaults" fullWidth>
      <div className={classes.cardBody}>
        <Text variant="subtitle2">Image Library</Text>
        <div className={classes.infoText}>
          {imageLibraryAuthorized && (
            <Text variant="body2">
              <b>Please note:</b> Location cannot be changed while an image library role is assigned
              to any host.
            </Text>
          )}
          <Text variant="body2">
            Using a shared file system location is a requirement for creating a highly available
            Image library setup. <LearnMoreLink url={imageLibraryStorageTypeDoc} />
          </Text>
        </div>
        <div className={classes.imageLibraryStorageOptions}>
          <PicklistField
            DropdownComponent={Dropdown}
            id="Location"
            label="Location"
            onChange={handleImageLibraryStorageTypeChange}
            items={imageLibraryStorageTypeOptions}
            value={imageLibraryStorageType}
            disabled={imageLibraryAuthorized}
            required
          />
          {imageLibraryStorageType !== ImageLibraryStorageType.VOLUME_TYPE ? (
            <>
              <TextField
                id="imageLibraryLocation"
                label="File System Path"
                placeholder="eg. /var/opt/imagelibrary/data"
                onChange={getParamsUpdater('imageLibraryLocation')}
                value={params.imageLibraryLocation}
                info="Location on your host file system where the image files from the image library will be stored"
                validations={[
                  imageLibraryLocationValidation,
                  requiredValidatorWithMessage(
                    'Field is required. Click on Set to default to reset to default path',
                  ),
                ]}
                disabled={imageLibraryAuthorized}
              />
              {!imageLibraryAuthorized && (
                <div
                  className={classes.setToDefault}
                  onClick={() =>
                    getParamsUpdater('imageLibraryLocation')(defaultImageLibraryLocation)
                  }
                  data-testid="set-image-library-location-default-button"
                >
                  <Text variant="caption1">Set to default</Text>
                  <HintTooltip message="Reset default path for image library location" />
                </div>
              )}
            </>
          ) : (
            <PicklistField
              DropdownComponent={AsyncDropdown}
              id="imageLibraryLocation"
              label="Volume Type"
              onChange={getParamsUpdater('imageLibraryLocation')}
              value={params.imageLibraryLocation}
              loading={loadingVolumeTypes}
              disabled={imageLibraryAuthorized}
              items={volumeTypeOptions}
              required
            />
          )}
        </div>
        <Divider className={classes.divider} />
        <Text variant="subtitle2">Hypervisor</Text>
        {hypervisorAuthorized && (
          <Text variant="body2">
            <b>Please note:</b> Virtual machine storage path may not be changed while a hypervisor
            role is assigned, and shared storage may not be changed while any VMs exist.
          </Text>
        )}
        <div className={classes.virtualMachineStorageSection}>
          <div className={classes.withSetToDefault}>
            <TextField
              id="vmStoragePath"
              label="Virtual Machine Storage Path"
              onChange={getParamsUpdater('vmStoragePath')}
              value={params.vmStoragePath}
              info="Location on your host file system where the virtual machine files will be stored"
              disabled={hypervisorAuthorized}
              required
            />
            {!hypervisorAuthorized && (
              <div
                className={classes.setToDefault}
                onClick={() => getParamsUpdater('vmStoragePath')(defaultVmStoragePath)}
                data-testid="set-vm-storage-path-default-button"
              >
                <Text variant="caption1">Set to default</Text>
                <HintTooltip message="Reset default path for virtual machine storage" />
              </div>
            )}
          </div>
          <ToggleSwitchField
            id="instanceSharedStorage"
            value={params.instanceSharedStorage}
            onChange={getParamsUpdater('instanceSharedStorage')}
            customTooltipInfoBody={
              <Text variant="body2" className={classes.switchTooltip}>
                Enable if this path is mounted as shared storage (e.g., NFS) across all hosts.{' '}
                <ExternalLink className={classes.tooltipLink} url={ephemeralSharedStorageDoc}>
                  Learn More
                </ExternalLink>
              </Text>
            }
            label="This is Shared Storage"
          />
        </div>
        <TextField
          id="vncFloatingIp"
          label="Console Proxy IP or Domain Name"
          onChange={getParamsUpdater('vncFloatingIp')}
          placeholder="eg. 118.214.206.170 or sub.domain.com"
          value={params.vncFloatingIp}
          info="Specify a floating IP or domain name to use as the VM console base URL. Please ensure that appropriate routing is done"
        />
      </div>
    </FormFieldCard>
  )
}
